/**
 * Скрипт для управления выпадающими меню с умными задержками
 *
 * Особенности:
 * - Показ меню с небольшой задержкой (50ms) для предотвращения случайных срабатываний
 * - Скрытие меню с большой задержкой (400ms) для комфортного перехода к подменю
 * - Плавные CSS анимации с transform для современного вида
 * - Очистка таймеров при быстрых переходах между элементами
 * - Управление с клавиатуры: Enter/Space на кнопке открывает и закрывает подменю,
 *   Escape закрывает, уход фокуса из пункта закрывает
 */

// Константы селекторов
const SELECTORS = {
  MENU_ITEMS_WITH_CHILDREN: ".menu-item-has-children",
  DESKTOP_MENU_SUBLIST: ".desktop-menu__sublist",
  TOGGLE: ".desktop-menu__link--toggle",
  SUBLIST_VISIBLE: "desktop-menu__sublist--visible",
};

export default class MenuDropdown {
  constructor() {
    // Настройки задержек (в миллисекундах)
    this.showDelay = 50; // Маленькая задержка перед показом (предотвращает случайные срабатывания)
    this.hideDelay = 600; // Большая задержка перед скрытием (дает время дотянуться до подменю)

    this.menuItems = document.querySelectorAll(SELECTORS.MENU_ITEMS_WITH_CHILDREN);
    this.init();
  }

  init() {
    this.menuItems.forEach((item) => {
      const submenu = item.querySelector(SELECTORS.DESKTOP_MENU_SUBLIST);

      if (submenu) {
        // Добавляем обработчики событий для наведения и ухода мыши
        item.addEventListener("mouseenter", () => this.showSubmenu(submenu));
        item.addEventListener("mouseleave", () => this.hideSubmenu(submenu));

        // Также добавляем обработчики для самого подменю
        submenu.addEventListener("mouseenter", () => this.showSubmenu(submenu));
        submenu.addEventListener("mouseleave", () => this.hideSubmenu(submenu));

        this.initKeyboard(item, submenu);
      }
    });
  }

  /**
   * Навигация с клавиатуры: кнопка-триггер, Escape, закрытие при уходе фокуса
   */
  initKeyboard(item, submenu) {
    const toggle = item.querySelector(SELECTORS.TOGGLE);

    if (toggle) {
      toggle.addEventListener("click", (event) => {
        // detail === 0 — активация с клавиатуры (Enter/Space), переключаем.
        // Клик мышью приходит уже после открытия по hover, его не переключаем.
        const isOpen = toggle.getAttribute("aria-expanded") === "true";
        if (event.detail === 0 && isOpen) {
          this.hideSubmenu(submenu, true);
        } else {
          this.showSubmenu(submenu);
        }
      });
    }

    item.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !toggle) return;

      this.hideSubmenu(submenu, true);
      toggle.focus();
    });

    item.addEventListener("focusout", (event) => {
      if (!item.contains(event.relatedTarget)) {
        this.hideSubmenu(submenu, true);
      }
    });
  }

  setExpanded(submenu, expanded) {
    const toggle = submenu.parentElement.querySelector(SELECTORS.TOGGLE);
    if (toggle) {
      toggle.setAttribute("aria-expanded", String(expanded));
    }
  }

  showSubmenu(submenu) {
    // Очищаем предыдущие таймеры
    clearTimeout(submenu.hideTimeout);
    clearTimeout(submenu.showTimeout);
    clearTimeout(submenu.displayTimeout);

    // Устанавливаем задержку перед показом для плавности
    submenu.showTimeout = setTimeout(() => {
      // Добавляем класс для показа меню
      submenu.classList.add(SELECTORS.SUBLIST_VISIBLE);

      // Устанавливаем стили для показа
      submenu.style.display = "block";
      submenu.style.opacity = "1";
      submenu.style.visibility = "visible";
      this.setExpanded(submenu, true);
    }, this.showDelay);
  }

  /**
   * @param {HTMLElement} submenu
   * @param {boolean} immediate - скрыть без задержки (для клавиатуры)
   */
  hideSubmenu(submenu, immediate = false) {
    // Очищаем таймер показа, если он был
    clearTimeout(submenu.showTimeout);
    clearTimeout(submenu.hideTimeout);

    // Устанавливаем более длинную задержку перед скрытием для комфорта пользователя
    submenu.hideTimeout = setTimeout(() => {
      this.setExpanded(submenu, false);
      submenu.classList.remove(SELECTORS.SUBLIST_VISIBLE);

      // Устанавливаем стили для скрытия
      submenu.style.opacity = "0";
      submenu.style.visibility = "hidden";

      // Скрываем элемент после завершения анимации
      submenu.displayTimeout = setTimeout(() => {
        submenu.style.display = "none";
      }, 300); // Увеличено время анимации для плавности
    }, immediate ? 0 : this.hideDelay);
  }

  /**
   * Метод для очистки всех таймеров (полезно при уничтожении компонента)
   */
  clearAllTimeouts() {
    this.menuItems.forEach((item) => {
      const submenu = item.querySelector(SELECTORS.DESKTOP_MENU_SUBLIST);
      if (submenu) {
        clearTimeout(submenu.showTimeout);
        clearTimeout(submenu.hideTimeout);
        clearTimeout(submenu.displayTimeout);
      }
    });
  }
}
